import {DataObject} from "./DataObject";
import {EcommerceConnection} from "./EcommerceConnection";
import {Product} from "./Product";
import {EcommerceProduct} from "./EcommerceProduct";

export interface Item {
  remoteProductId: string;
  ecommerceProductId: number;
  ecommerceProduct: EcommerceProduct;
  source: string;
  productId: number;
  product: Product;
  status: string;
  quantity: number;
  title: string;
  /**
   * The seller's own code for the line as it arrived from the ecommerce
   * platform. Not every gateway sends one, so it is optional -- matching falls
   * back to the title when it is absent.
   */
  sku?: string;
}

export class EcommerceOrder implements DataObject {
  ID: number;
  gatewayagenttype: string;
    ecommerceconnection_ID: number;
  remoteOrderId: string;
  /**
   * DEAD ON THE WIRE. The server confirmed in
   * docs/contracts/inbox/_processed/2026-09-11-ecommerce-order-shipment-update-status-answers.md
   * (section 3) that this column does not exist on its `EcommerceOrder` entity
   * and will not start being sent -- it resolves the Order by
   * `ecommerceconnection_ID` + `customerorderid = remoteOrderId` instead. It
   * therefore stays at its `0` default for every row. Kept only so the old join
   * keeps compiling; use `printNodeOrderIds` for the fulfilment link.
   */
  order_ID: number;
  /**
   * The ids of the PrintNodeOrders fulfilling this order.
   *
   * The server sends this on **every** response and in the notification
   * payload, **always as an array, never `null`, never omitted** -- so an
   * `undefined` here means an old server, not "no answer". Confirmed in
   * docs/contracts/inbox/_processed/2026-09-12-printnodeorderids-crossed-messages-and-cancelled-fulfilments.md
   *
   *   undefined  old server -- the only case that is genuinely UNKNOWN
   *   []         definite: no fulfilments. NOT_SHIPPED, and what the server's
   *              409 refuses a resend on
   *   [1,2,3]    authoritative and COMPLETE -- unfiltered by status
   *
   * It is computed per response, not stored: a denormalised column would drift,
   * because the legacy system also creates PrintNodeOrders and would not know
   * to maintain it. So there is no DDL behind it and it cannot be written back.
   *
   * Because the set is unfiltered, CANCELLED fulfilments ARE included and the
   * client filters them itself -- see
   * `EcommerceOrdersComponent.isPrintNodeOrderCancelled()`. Do not default this
   * to `[]`: that would turn "old server" into a false "no fulfilments".
   */
  printNodeOrderIds?: number[];
  remoteOrderState: string;
  rawmessage: string;
  bookorder: any;
  // processingState: string;
  // processingStatus: string;
  // orderCreated: string;
  // orderCreatedMessage: string;
  // userStatus: string;
  userNotes: string | null;
  processingReason: string | null;
  lastupdated: Date;
  title: string | null;
  firstname: string | null;
  lastname: string | null;
  company: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  shippingInfo: string | null;
  phonecountry: string | null;
  phone: string | null;
  email: string | null;
  postalcode: string | null;
  createdDate: Date;
  // overrideRemoteOrderState: string;
  ignoreStatus: string;

  // ---------------------------------------------------------------------------
  // Roll-ups of the PrintNodeOrders fulfilling this order.
  //
  // These are the server's own summary of facts that live on the fulfilments.
  // As of 2026-09-13 the server does NOT populate any of them -- it confirmed
  // on 2026-09-11 that `productionStatus` and `shippedTimestamp` are not on its
  // entity. Population is requested in
  // outbox/2026-09-13-ecommerce-order-shipped-rollup-fields.md
  //
  // Until that lands, every field here holds its constructor default, so
  // anything reading them must tolerate "never set" as the normal case. Nothing
  // in the shipped DECISION reads them any more -- that is computed from
  // `printNodeOrderIds` -- so they are display-only.
  // ---------------------------------------------------------------------------
  trackingNumbers: string;
  productionStatus: string;
  // status: string; // TODO: Add property to DB and back end
  dateStartedProduction: Date;
  /**
   * When the LAST PrintNodeOrder shipped -- i.e. the greatest of their
   * `shippedTimestamp` values, not the first.
   *
   * The greatest is the only defensible choice: the order is not shipped until
   * every fulfilment is, so the earliest would report an order as complete
   * while part of it was still in production.
   */
  shippedTimestamp: Date;
  /**
   * Roll-up of the fulfilments' shipping state, mirroring
   * `PrintNodeOrder.shippingStatus` (`NONE` / `SHIPPED` / `CANCELLED` ...).
   *
   * Requested, not yet sent. Do NOT use it to decide whether the resend button
   * appears -- that is `getShippedState()`, which works off `printNodeOrderIds`
   * and stays authoritative because it can tell "not shipped" from "cannot
   * see the fulfilments". This field is for display and filtering.
   */
  shippingStatus: string;

  ecommerceOrderStatus: string;
  // Tracks the push of the "order complete + tracking numbers" message back to
  // the remote ecommerce platform.
  //
  //   INITIAL  nothing has been pushed -- the resting state, deliberately NOT
  //            rendered, since almost every row sits here for its whole life
  //   WAITING  queued for a later sweep. Not produced by the current server
  //            strategy; if it appears, it means "queued", NOT "failed"
  //   SENT     the completion and tracking numbers reached the remote store
  //   ERROR    the push was attempted and failed -- the one worth a colour
  //
  // Confirmed by the server in
  // docs/contracts/inbox/_processed/2026-09-11-ecommerce-order-shipment-update-status-answers.md
  //
  // NOTE: Order.shipmentUpdateStatus has the SAME name but a NARROWER enum
  // (WAITING | SENT | ERROR, no INITIAL) and is a different row. A value legal
  // on one is not necessarily legal on the other -- do not share code that
  // switches on them.
  shipmentUpdateStatus: string;
  addressStatus: string;
  productStatus: string;
  carrierServiceLevelStatus: string;
  functionalStatus: string;
  costVsRevenueStatus: string;
  userHoldStatus: string;
  releaseStatus: string;
  revenueStatus: string;
  uniqueRemoteOrderIdStatus: string;
  remoteOrderStateStatus: string;
  facilityOptions: any[];
  productionCost: number;
  shippingCost: number;
  packingCost: number;
  taxCost: number;
  productRevenue: number;
  shippingRevenue: number;
  taxRevenue: number;
  ecommerceConnection: EcommerceConnection;

  constructor( obj: any ) {
    if (obj && obj['ID'] ) {
      this.ID = obj['ID'];
    } else {
      this.ID = 0;
    }

    this.gatewayagenttype = '';
    this.ecommerceconnection_ID = 0;
    this.remoteOrderId = '';
    this.remoteOrderState = '';
    this.rawmessage = '';
    this.bookorder = {};
    // this.processingState = '';
    // this.processingStatus = '';
    // this.orderCreated = '';
    // this.orderCreatedMessage = '';
    // this.userStatus = '';
    this.userNotes = '';
    this.processingReason = '';
    this.lastupdated = new Date(0);
    this.title = '';
    this.firstname = '';
    this.lastname = '';
    this.company = '';
    this.address1 = '';
    this.address2 = '';
    this.address3 = '';
    this.address4 = '';
    this.city = '';
    this.state = '';
    this.country = '';
    this.shippingInfo = '';
    this.phonecountry = '';
    this.phone = '';
    this.email = '';
    this.postalcode = '';
    this.createdDate = new Date(0);
    // this.overrideRemoteOrderState = '';
    this.ecommerceConnection = undefined;

    this.order_ID = 0;
    // NOT [] -- see the property comment. Absent must stay distinguishable
    // from empty.
    this.printNodeOrderIds = undefined;
    this.trackingNumbers = '';
    this.productionStatus = 'INITIAL'; // TODO: Better default state
    // 'NONE' matches PrintNodeOrder's own resting value, so a row the server
    // has not populated reads as "not shipped" rather than as an empty string
    // that no switch handles.
    this.shippingStatus = 'NONE';
    // this.carrier = '';
    // this.serviceLevel = '';
    this.dateStartedProduction = new Date(0);

    this.ecommerceOrderStatus = 'INITIAL';
    this.shipmentUpdateStatus = 'INITIAL';
    this.addressStatus = 'INITIAL';
    this.productStatus = 'INITIAL';
    this.carrierServiceLevelStatus = 'INITIAL';
    this.functionalStatus = 'INITIAL';
    this.costVsRevenueStatus = 'INITIAL';
    this.revenueStatus = 'INITIAL';
    this.userHoldStatus = 'NO_HOLD';
    this.releaseStatus = 'INITIAL';
    this.uniqueRemoteOrderIdStatus = 'INITIAL';
    this.remoteOrderStateStatus = 'INITIAL';
    this.ignoreStatus = 'NOT_IGNORE';
    this.facilityOptions = [];

    this.productionCost = 0;
    this.packingCost = 0;
    this.shippingCost = 0;
    this.taxCost = 0;
    this.productRevenue = 0;
    this.shippingRevenue = 0;
    this.taxRevenue = 0;

    this.update(obj);
  }

  getId () {
    return this.ID;
  }

  getOrderValue () {
    return this.ID;
  }

  update (obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        // Handled ahead of the scalar branch on purpose: if the server ever
        // sends this as a comma-joined string, the scalar branch below would
        // happily assign a string to a number[] and every consumer would break
        // silently. Normalise it here instead.
        if ( prop === 'printNodeOrderIds' ) {
          this.printNodeOrderIds = EcommerceOrder.parsePrintNodeOrderIds(obj[prop]);
          continue;
        }
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        } else {
          if ( prop === 'facilityOptions' ) {
            if ( obj[prop] instanceof Object ) {
              this.facilityOptions = obj[prop];
            } else {
              if ( obj[prop] ) {
                this.facilityOptions = JSON.parse(obj[prop]);
              }
            }
          }
          if ( prop === 'bookorder') {
            if ( obj[prop] instanceof Object ) {
              this.bookorder = obj[prop];
            } else {
              if ( obj[prop] ) {
                this.bookorder = JSON.parse(obj[prop]);
              }
            }
          }
        }
        ['lastupdated', 'createdDate', 'dateStartedProduction', 'shippedTimestamp'].forEach( propertyName => {
          if ( this[propertyName] && !( this[propertyName] instanceof Date ) ) {
            try {
              this[propertyName] = new Date( obj[propertyName] );
            } catch ( e ) {
              console.error( e );
            }
          }
        });
      }
    }
  }

  /**
   * Accepts every shape the link could plausibly arrive in -- a real array, a
   * JSON array in a string column, or a comma-joined list -- and returns
   * `undefined` for anything it cannot read.
   *
   * `undefined` for unreadable input rather than `[]` is the important part:
   * `[]` would be read downstream as "this order has no fulfilments", which for
   * a shipped order would wrongly withhold the resend button, and for the
   * server's 409 rule is a different error entirely.
   */
  static parsePrintNodeOrderIds (value: any): number[] | undefined {
    if ( value === null || value === undefined ) {
      return undefined;
    }
    let raw: any = value;
    if ( typeof raw === 'string' ) {
      const trimmed = raw.trim();
      if ( trimmed === '' ) {
        return undefined;
      }
      if ( trimmed.charAt(0) === '[' ) {
        try {
          raw = JSON.parse(trimmed);
        } catch ( e ) {
          console.error( 'EcommerceOrder.printNodeOrderIds was not parseable JSON', e );
          return undefined;
        }
      } else {
        raw = trimmed.split(',');
      }
    }
    if ( !Array.isArray(raw) ) {
      return undefined;
    }
    const ids: number[] = [];
    for ( const entry of raw ) {
      const id = Number(entry);
      // A NaN or 0 id cannot match a real PrintNodeOrder, and letting it
      // through would make the "did we find them all?" count lie.
      if ( !isNaN(id) && id > 0 ) {
        ids.push(id);
      }
    }
    return ids;
  }

  getItems(): Item[] {
    if ( this.bookorder && this.bookorder.items ) {
      return this.bookorder.items;
    }
    return [];
  }

  toJSON() {
    const obj = {};
    for (const prop of Object.keys(this)) {
      if (this[prop] !== null && ( typeof this[prop] === 'string' || typeof this[prop] === 'number' || typeof this[prop] === 'boolean' || this[prop] instanceof String || this[prop] instanceof Boolean || this[prop] instanceof Number || this[prop] instanceof Date )) {
        obj[prop] = this[prop];
      } else {
        if ( prop === 'facilityOptions' && this.facilityOptions ) {
          obj[prop] = this.facilityOptions;
        }
        if ( prop === 'printNodeOrderIds' && this.printNodeOrderIds ) {
          // Server-derived and read-only here, but round-tripping it keeps a
          // save from looking like a deletion of the field.
          obj[prop] = this.printNodeOrderIds.slice();
        }
        if ( prop === 'bookorder' && this.bookorder ) {
          obj[prop] = this.bookorder;
          if ( obj[prop] && obj[prop].items ) {
            for ( const item of obj[prop].items ) {
              if ( item.product ) {
                delete item.product;
              }
              if ( item.ecommerceProduct ) {
                delete item.ecommerceProduct;
              }
            }
          }
        }
      }
    }
    return obj;
  }
}
